import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

const CheckboxGroup = ({
  groupLabel,
  options = [],
  value = [],
  onChange,
  inline = false,
  labelPosition = 'right',
  groupLabelClassName = '',
  checkboxContainerClassName = '',
  checkboxLabelClassName = '',
  checkboxInputClassName = '',
}) => {
  const [otherValue, setOtherValue] = useState('');
  const [isOtherCheckedInternal, setIsOtherCheckedInternal] = useState(false);
  const OTHER_VALUE_SUFFIX = '     '; // Five spaces as the suffix

  useEffect(() => {
    const initialSuffixedValue = value.find(val => typeof val === 'string' && val.endsWith(OTHER_VALUE_SUFFIX));
    const isOtherInitiallyChecked = value.some(val => String(val).toLowerCase() === 'other');
    if (initialSuffixedValue || isOtherInitiallyChecked || isOtherCheckedInternal) {
      setIsOtherCheckedInternal(true);
    }
    if (initialSuffixedValue) {
      setOtherValue(initialSuffixedValue.substring(0, initialSuffixedValue.length - OTHER_VALUE_SUFFIX.length));
    }
    else {
      setOtherValue('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, OTHER_VALUE_SUFFIX]);

  const handleCheckboxChange = (event) => {
    const { value: checkboxValue, checked } = event.target;
    console.log(`Checkbox value: ${checkboxValue}, checked: ${checked}`);
    const isOtherOption = String(checkboxValue).toLowerCase() === 'other';
    let newSelectedValues = [...value];

    if (isOtherOption) {
      setIsOtherCheckedInternal(checked);

      if (checked) {
        return;
      }
      else {
        newSelectedValues = newSelectedValues.filter((val) => !String(val).endsWith(OTHER_VALUE_SUFFIX));
        setOtherValue('');
      }
    }
    else {
      const isNumeric = options.some(
        (opt) => typeof opt?.value === 'number' || typeof opt?.label === 'number' || typeof opt === 'number'
      );
      const currentVal = isNumeric ? Number(checkboxValue) : checkboxValue;
      if (checked) {
        if (!newSelectedValues.includes(currentVal)) {
          newSelectedValues.push(currentVal);
        }
      }
      else {
        newSelectedValues = newSelectedValues.filter((val) => val !== currentVal);
      }
    }
    // Filter out 'other' only when calling onChange
    onChange(newSelectedValues);
  };
  
  const handleOtherInputChange = (e) => {
    const newTextValue = e.target.value;
    setOtherValue(newTextValue);

    let newSelectedValues = [...value];

    newSelectedValues = newSelectedValues.filter(val => !String(val).endsWith(OTHER_VALUE_SUFFIX));

    if (isOtherCheckedInternal && newTextValue) {
      newSelectedValues.push(`${newTextValue}${OTHER_VALUE_SUFFIX}`);
    }

    onChange(newSelectedValues);
  };

  const containerStyle = inline ? { display: 'flex', gap: '0.5rem', flexWrap: 'wrap' } : {};
  const itemStyle = inline ? {} : { display: 'block', marginBottom: '0.5rem' };

  return (
    <div className="checkbox-group-container">
      {groupLabel && <label className={`checkbox-group-label ${groupLabelClassName}`}>{groupLabel}</label>}
      <div style={containerStyle}>
        {options.map((option) => {
          const optionValue = option?.value ?? option?.label ?? option;
          const optionLabel = option?.label ?? option;
          const isOtherOption = String(optionValue).toLowerCase() === 'other';
          const isChecked = isOtherOption
            ? isOtherCheckedInternal
            : value.includes(optionValue);
          const checkboxId = `checkbox-${groupLabel}-${optionValue}`;

          return (
            <div key={optionValue} style={itemStyle}
              className={`checkbox-item-container ${checkboxContainerClassName}`}
            >
              {labelPosition === 'left' && (
                <label htmlFor={checkboxId} className={`checkbox-label ${checkboxLabelClassName}`}>
                  {optionLabel}
                </label>
              )}
              <input
                type="checkbox"
                id={checkboxId}
                value={optionValue}
                checked={isChecked}
                onChange={handleCheckboxChange}
                className={`checkbox-input ${checkboxInputClassName}`}
                style={labelPosition === 'left' ? { marginLeft: '0.5rem' } : null }
              />
              {labelPosition === 'right' && (
                <label htmlFor={checkboxId} className={`checkbox-label ${checkboxLabelClassName}`}>
                  {optionLabel}
                </label>
              )}
              {isOtherOption && (
                <input
                  type="text"
                  value={otherValue}
                  onChange={handleOtherInputChange}
                  className="other-input"
                  placeholder="Please specify"
                  disabled={!isOtherCheckedInternal}
                  style={!isOtherCheckedInternal ? { cursor: 'not-allowed', marginLeft: '0.5rem' } : null }
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

CheckboxGroup.propTypes = {
  groupLabel: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.any.isRequired,
    })
  ).isRequired,
  value: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  inline: PropTypes.bool,
  labelPosition: PropTypes.oneOf(['left', 'right']),
  groupLabelClassName: PropTypes.string,
  checkboxContainerClassName: PropTypes.string,
  checkboxLabelClassName: PropTypes.string,
  checkboxInputClassName: PropTypes.string,
};

export default CheckboxGroup;